// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Override this widget to force the zone attached under each column in the grid to fill its parent height,
 * This will help us aligning grid content vertically across multiple columns.
 * Also, enhance the responsivity of this grid.
 */

/* eslint-disable spire/export-styles */
import { getFocalPointStyles } from "@insite/client-framework/Common/BasicSelectors";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import Zone from "@insite/client-framework/Components/Zone";
import { ColumnAlignment } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemStyle, GridWidths } from "@insite/mobius/GridItem";
import getColor from "@insite/mobius/utilities/getColor";
import setFullWidth from "@mcmc/App/Handlers/Layout/setFullWidth";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import allColors from "@mcmc/XDL/PreStyle/colors";
import { layoutContainer } from "@mcmc/XDL/Utilities";
import { mergeWithCss } from "@mcmc/XDL/Utilities";
import { getAllColorsAsDropdownOptions } from "@mcmc/XDL/Utilities/getAllColorsAsDropdownOptions";
import * as cssLinter from "css";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: McMcApplicationState) => ({
    fullWidth: state.xLayout.isFullWidth,
});

const mapDispatchToProps = { setFullWidth };

const enum fields {
    columns = "columns",
    gap = "gap",
    alignment = "alignment",
    reflow = "reflow",
    container = "container",
    isFullWidth = "isFullWidth",
    fullWidthStylingType = "fullWidthStylingType",
    fullWidthImage = "fullWidthImage",
    fullWidthImageFocalPoint = "fullWidthImageFocalPoint",
    hasBgColor = "hasBgColor",
    bgColor = "bgColor",
    padding = "padding",
    margin = "margin",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.columns]: GridWidths[];
        [fields.gap]: number;
        [fields.alignment]: ColumnAlignment[];
        [fields.reflow]: boolean;
        [fields.container]: boolean;
        [fields.isFullWidth]: boolean;
        [fields.fullWidthStylingType]: string;
        [fields.fullWidthImage]: string;
        [fields.fullWidthImageFocalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        [fields.customCSS]: string;
        [fields.hasBgColor]: boolean;
        [fields.bgColor]: string;
        [fields.padding]: string;
        [fields.margin]: string;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mobileColumnsRef: { [key: number]: GridWidths | GridWidths[] } = {
    1: [3, 3, 1, 1, 1],
    2: [4, 4, 2, 2, 2],
    3: [12, 6, 6, 3, 3], // <xng-change on xs let the column fill all the available width />
    4: [12, 6, 6, 4, 4], // <xng-change on xs let the column fill all the available width />
    5: [12, 12, 5, 5, 5],
    6: [12, 12, 6, 6, 6],
    7: [12, 12, 7, 7, 7],
    8: [12, 12, 8, 8, 8],
    9: [12, 12, 9, 9, 9],
    10: [12, 12, 10, 10, 10],
    11: [12, 12, 11, 11, 11],
    12: 12,
};

// <xng-change force the zone inside the grid item to fill its parent height>
const wrapperCss = css`
    & > div {
        height: 100%;
    }
`;
// <xng-change>

const convertToResponsive = (column: GridWidths): GridWidths | GridWidths[] => {
    return mobileColumnsRef[column] || column;
};

const Row = (props: Props) => {
    const {
        fields: {
            columns,
            gap,
            alignment,
            reflow,
            container,
            isFullWidth,
            fullWidthStylingType,
            fullWidthImage,
            fullWidthImageFocalPoint,
            customCSS,
            hasBgColor,
            bgColor,
            padding,
            margin,
        },
        fullWidth,
        setFullWidth,
        id,
    } = props;

    useEffect(() => {
        if (container && isFullWidth) {
            setFullWidth({
                isFullWidth: true,
                widgetId: id,
            });
            return () => {
                setFullWidth({
                    isFullWidth: false,
                    widgetId: id,
                });
            };
        }
    }, [container, isFullWidth]);
    if (container && isFullWidth) {
        if (!fullWidth) {
            setFullWidth({
                isFullWidth: true,
                widgetId: id,
            });
        }
    }

    const fullWidthBackgroundCss = !isFullWidth
        ? undefined
        : fullWidthStylingType === "image"
        ? css`
              background-image: url("${parseImageUrl(fullWidthImage).url}");
              background-size: cover;
              ${getFocalPointStyles(fullWidthImageFocalPoint)}
          `
        : undefined;

    const finalCss = css`
        ${customCSS}
    `;

    const gridStyles = [
        GridContainer.defaultProps?.css,
        css`
            ${GridOffset} {
                @media print {
                    ${typeof document !== "undefined" &&
                    navigator?.userAgent.search("Firefox") > -1 &&
                    columns.length === 1 &&
                    css`
                        float: left;
                    `}
                }
            }
        `,
        finalCss,
    ];
    if (container && (fullWidth || isFullWidth)) {
        gridStyles.push(layoutContainer);
        gridStyles.push(fullWidthBackgroundCss);
    }

    if (padding) {
        gridStyles.push(
            css`
                padding: ${padding};
            `,
        );
    }

    if (margin) {
        gridStyles.push(
            css`
                margin: ${margin};
            `,
        );
    }

    if (hasBgColor) {
        gridStyles.push(
            css`
                background-color: ${getColor(bgColor)};
            `,
        );
    }

    const columnElements: JSX.Element[] = [];

    for (let x = 0; x < columns.length; x += 1) {
        const align = alignment[x] ? alignment[x] : "top";
        columnElements.push(
            <GridItem
                key={x}
                width={reflow ? convertToResponsive(columns[x]) : columns[x]}
                align={align}
                css={[
                    GridItem.defaultProps?.css,
                    align === "top" ? wrapperCss : undefined,
                    css`
                        @media print {
                            float: left;
                            ${columns.length > 3 &&
                            css`
                                width: 33%;
                                ${typeof document !== "undefined" && navigator?.userAgent.search("Firefox") === -1
                                    ? css`
                                          break-inside: avoid;
                                      `
                                    : css`
                                          float: none;
                                          margin-bottom: 15px !important;
                                      `}
                            `}
                        }
                    `,
                ]} // <xng-change />
            >
                <Zone contentId={id} zoneName={`Content${x}`} />
            </GridItem>,
        );
    }

    return (
        <GridContainer gap={gap || 0} css={gridStyles} className={container && isFullWidth ? "full-width" : ""}>
            {columnElements}
        </GridContainer>
    );
};

const defaultCustomCss = `& {

}`;

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(Row),
    definition: {
        group: "Basic",
        icon: "distribute-spacing-vertical",
        fieldDefinitions: [
            {
                name: fields.columns,
                displayName: "Layout",
                editorTemplate: "ColumnsField",
                defaultValue: [4, 4, 4],
                fieldType: "General",
                sortOrder: 1,
                validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
            },
            {
                name: fields.reflow,
                displayName: "Use default reflow for responsive screens sizes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 13,
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 2,
            },
            {
                name: fields.alignment,
                displayName: "Column Alignment",
                editorTemplate: "ColumnAlignmentField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                tab: advancedTab,
            },
            {
                name: fields.padding,
                displayName: "Padding",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip:
                    "The value should be a valid css padding, for example: 5px will add padding to all directions, 5px 0 will add padding to top and bottom.",
                validate: (value: string) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("padding", value) ? null : `Invalid Input`;
                },
                tab: advancedTab,
                sortOrder: 4,
            },
            {
                name: fields.margin,
                displayName: "Margin",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip:
                    "The value should be a valid css margin, for example: 5px will add margin to all directions, 5px 0 will add margin to top and bottom.",
                validate: (value: string) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("margin", value) ? null : `Invalid Input`;
                },
                tab: advancedTab,
                sortOrder: 5,
            },
            {
                name: fields.container,
                displayName: "Container",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: advancedTab,
                sortOrder: 6,
            },
            {
                name: fields.isFullWidth,
                displayName: "Full Width",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: item => item.fields.container,
                tab: advancedTab,
                sortOrder: 7,
            },
            {
                name: fields.hasBgColor,
                displayName: "Change Background Color",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: item => item.fields.container,
                tab: advancedTab,
                sortOrder: 8,
            },
            {
                name: fields.bgColor,
                displayName: "Background Color",
                editorTemplate: "DropDownField",
                options: getAllColorsAsDropdownOptions(allColors),
                defaultValue: "primary.main",
                fieldType: "General",
                isVisible: item => item.fields.hasBgColor,
                tab: advancedTab,
                sortOrder: 10,
            },
            {
                name: fields.fullWidthStylingType,
                displayName: "Styling",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Solid Color",
                        value: "solidColor",
                    },
                    {
                        displayName: "Image",
                        value: "image",
                    },
                ],
                defaultValue: "solidColor",
                horizontal: true,
                fieldType: "General",
                isVisible: item => item.fields.isFullWidth && item.fields.hasBgColor,
                tab: advancedTab,
                sortOrder: 9,
            },
            {
                name: fields.fullWidthImage,
                displayName: "Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isVisible: ({ fields: { isFullWidth, fullWidthStylingType } }) =>
                    !!isFullWidth && fullWidthStylingType === "image",
                tab: advancedTab,
                sortOrder: 11,
            },
            {
                name: fields.fullWidthImageFocalPoint,
                displayName: "Focal Point",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top Left", value: "topLeft" },
                    { displayName: "Top Center", value: "topCenter" },
                    { displayName: "Top Right", value: "topRight" },
                    { displayName: "Center Left", value: "centerLeft" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Center Right", value: "centerRight" },
                    { displayName: "Bottom Left", value: "bottomLeft" },
                    { displayName: "Bottom Center", value: "bottomCenter" },
                    { displayName: "Bottom Right", value: "bottomRight" },
                ],
                defaultValue: "center",
                fieldType: "General",
                hideEmptyOption: true,
                isVisible: ({ fields: { isFullWidth, fullWidthStylingType } }) =>
                    !!isFullWidth && fullWidthStylingType === "image",
                tab: advancedTab,
                sortOrder: 12,
            },
            {
                name: "customCSS",
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
